import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Button, Offcanvas, Form, Dropdown, Modal } from "react-bootstrap";
import { FiUpload, FiDownload, FiSearch, FiMoreVertical, FiTrash2, FiEye, FiFile } from "react-icons/fi";
import ReactPaginate from 'react-paginate';
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

const ImportExportList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [previewData, setPreviewData] = useState({
        type: '', // 'quotation', 'po', 'invoice'
        title: '',
        fileUrl: '',
        refNo: '',
        previewType: '' // 'pdf', 'image', 'doc'
    });

    // Upload state
    const [uploadData, setUploadData] = useState({
        quotation_file: null,
        quotation_refno: "",
        po_file: null,
        po_number: "",
        tax_invoice_file: null,
        tax_invoice_refno: ""
    });

    const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        fetchData();
    }, [currentPage, searchQuery]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${API_BASE_URL}/api/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page: currentPage + 1,
                    search: searchQuery,
                    import_export: true
                }
            });

            if (response.data.success) {
                setData(response.data.data);
                setPageCount(response.data.meta.last_page);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(0);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected);
    };

    const openUploadModal = (item) => {
        setSelectedItem(item);
        setUploadData({
            quotation_file: null,
            quotation_refno: item.ref_no || "",
            po_file: null,
            po_number: item.deals?.[0]?.po_number || "",
            tax_invoice_file: null,
            tax_invoice_refno: item.tax_invoices?.[0]?.ref_no || ""
        });
        setShowUploadModal(true);
    };

    const openPreview = (item, type) => {
        let fileUrl = '';
        let title = '';
        let refNo = '';

        switch (type) {
            case 'quotation':
                if (item.quotation_file) {
                    fileUrl = `${API_BASE_URL}/storage/${item.quotation_file}`;
                    title = 'Quotation Document';
                    refNo = item.ref_no || 'Quotation';
                } else {
                    Swal.fire({
                        icon: 'info',
                        title: 'No Document',
                        text: 'Quotation document not uploaded yet',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    return;
                }
                break;

            case 'po':
                if (item.deals?.[0]?.po_attachment) {
                    fileUrl = `${API_BASE_URL}/storage/${item.deals[0].po_attachment}`;
                    title = 'Purchase Order';
                    refNo = item.deals[0].po_number || 'PO';
                } else if (item.po_file) {
                    fileUrl = `${API_BASE_URL}/storage/${item.po_file}`;
                    title = 'Purchase Order';
                    refNo = item.deals?.[0]?.po_number || 'PO';
                } else {
                    Swal.fire({
                        icon: 'info',
                        title: 'No Document',
                        text: 'Purchase Order document not uploaded yet',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    return;
                }
                break;

            case 'invoice':
                const invoice = item.tax_invoices?.[0] || item.proforma_invoices?.[0];
                if (invoice?.tax_invoice_file) {
                    fileUrl = `${API_BASE_URL}/storage/${invoice.tax_invoice_file}`;
                    title = 'Tax Invoice';
                    refNo = invoice.ref_no || 'Invoice';
                } else {
                    Swal.fire({
                        icon: 'info',
                        title: 'No Document',
                        text: 'Invoice document not uploaded yet',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    return;
                }
                break;
        }

        // Determine file type for preview
        const fileExtension = fileUrl.split('.').pop().toLowerCase();
        let previewType = '';

        if (['pdf'].includes(fileExtension)) {
            previewType = 'pdf';
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)) {
            previewType = 'image';
        } else if (['doc', 'docx', 'txt'].includes(fileExtension)) {
            previewType = 'doc';
        } else {
            previewType = 'other';
        }

        setPreviewData({
            type,
            title,
            fileUrl,
            refNo,
            previewType
        });
        setShowPreviewModal(true);
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return 'Document';
        const parts = url.split('/');
        return parts[parts.length - 1];
    };

    const handleFileChange = (e, field) => {
        setUploadData(prev => ({ ...prev, [field]: e.target.files[0] }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUploadData(prev => ({ ...prev, [name]: value }));
    };

    const handleExport = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${API_BASE_URL}/api/export`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, `quotations_${dayjs().format('YYYY-MM-DD')}.xlsx`);

            Swal.fire({
                icon: 'success',
                title: 'Export Success',
                text: 'Quotations exported successfully',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire("Error", "Failed to export data", "error");
        }
    };

    // const handleImport = async (e) => {
    //     const file = e.target.files[0];
    //     if (!file) return;

    //     const formData = new FormData();
    //     formData.append("file", file);

    //     setLoading(true);
    //     try {
    //         const userInfo = JSON.parse(localStorage.getItem('user-info'));
    //         const origin = localStorage.getItem('user-origin');
    //         const token = userInfo?.token;

    //         const response = await axios.post(`${API_BASE_URL}/api/import`, formData, {
    //             headers: {
    //                 'Authorization': `Bearer ${token}`,
    //                 'Tenant-Origin': origin,
    //                 'Content-Type': 'multipart/form-data'
    //             }
    //         });

    //         const result = response.data;
    //         const { summary, skipped_rows } = result.data || {};

    //         if (summary?.imported > 0 || summary?.updated > 0) {
    //             Swal.fire({
    //                 icon: 'success',
    //                 title: 'Import Result',
    //                 html: `Records Imported: ${summary.imported}<br/>Records Updated: ${summary.updated}` +
    //                     (skipped_rows?.length > 0 ? `<br/><br/><small class="text-danger">${skipped_rows.length} rows were skipped.</small>` : ''),
    //                 toast: true,
    //                 position: 'top-end',
    //                 showConfirmButton: false,
    //                 timer: 5000
    //             });
    //             fetchData();
    //         } else if (skipped_rows?.length > 0) {
    //             Swal.fire({
    //                 icon: 'warning',
    //                 title: 'Import Skipped',
    //                 html: `<div style="text-align: left;">` +
    //                     `<strong>${result.message}</strong><br/><br/>` +
    //                     `<div style="max-height: 200px; overflow-y: auto; font-size: 0.85rem;">` +
    //                     skipped_rows.map(row => `<div>${row}</div>`).join('') +
    //                     `</div></div>`,
    //                 confirmButtonText: 'Close',
    //             });
    //         } else {
    //             Swal.fire({
    //                 icon: 'success',
    //                 title: 'Import Success',
    //                 text: result.message || 'Quotations imported successfully',
    //                 toast: true,
    //                 position: 'top-end',
    //                 showConfirmButton: false,
    //                 timer: 3000
    //             });
    //             fetchData();
    //         }
    //     } catch (error) {
    //         console.error("Import error:", error);
    //         Swal.fire("Error", "Failed to import data", "error");
    //     } finally {
    //         setLoading(false);
    //         e.target.value = ""; // Reset input
    //     }
    // };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${API_BASE_URL}/api/import`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const result = response.data;
            const { summary, skipped_rows } = result.data || {};

            // Check for specific "client not found" errors
            const clientNotFoundErrors = skipped_rows?.filter(row =>
                row.includes("Client '") && row.includes("' not found")
            ) || [];

            // Check for successful imports
            const hasSuccessImports = summary?.imported > 0 || summary?.updated > 0;

            if (hasSuccessImports) {
                // Show success with optional warning about missing clients
                if (clientNotFoundErrors.length > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `Records Imported: ${summary.imported}<br/>Records Updated: ${summary.updated}` +
                            `<br/><br/><div style="text-align: left; font-size: 0.9rem; max-height: 150px; overflow-y: auto;">` +
                            `<strong>Note:</strong> ${clientNotFoundErrors.length} clients were not found:<br/>` +
                            clientNotFoundErrors.map(row => `<div class="text-danger">• ${row.replace('❌ ', '')}</div>`).join('') +
                            `</div>`,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Import Result',
                        html: `Records Imported: ${summary.imported}<br/>Records Updated: ${summary.updated}` +
                            (skipped_rows?.length > 0 ? `<br/><br/><small class="text-warning">${skipped_rows.length} rows had issues.</small>` : ''),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 5000
                    });
                }
                fetchData();
            } else if (skipped_rows?.length > 0) {
                // No successful imports, only skipped rows
                if (clientNotFoundErrors.length > 0) {
                    // Show special toast for client not found
                    Swal.fire({
                        icon: 'error',
                        title: 'Client Not Found',
                        html: `<div style="text-align: left;">` +
                            `<p><strong>${clientNotFoundErrors.length} clients not found:</strong></p>` +
                            `<div style="max-height: 200px; overflow-y: auto; font-size: 0.85rem;">` +
                            clientNotFoundErrors.map(row => `<div class="text-danger">• ${row.replace('❌ ', '')}</div>`).join('') +
                            `</div><br/>` +
                            `<p><small>Please create these clients first or enable "Create missing clients" option.</small></p>` +
                            `</div>`,
                        confirmButtonText: 'OK',
                        showCancelButton: true,
                        cancelButtonText: 'Cancel',
                    });
                } else {
                    // Other types of skipped rows
                    Swal.fire({
                        icon: 'warning',
                        title: 'Import Issues',
                        html: `<div style="text-align: left;">` +
                            `<strong>${result.message}</strong><br/><br/>` +
                            `<div style="max-height: 200px; overflow-y: auto; font-size: 0.85rem;">` +
                            skipped_rows.map(row => `<div>${row}</div>`).join('') +
                            `</div></div>`,
                        confirmButtonText: 'Close',
                    });
                }
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'No Data Processed',
                    text: 'No records were imported or updated',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
            }
        } catch (error) {
            console.error("Import error:", error);
            Swal.fire({
                icon: 'error',
                title: 'Import Failed',
                text: error.response?.data?.message || "Failed to import data",
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
            e.target.value = ""; // Reset input
        }
    };
    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append("id", selectedItem.id);
        if (uploadData.quotation_file) formData.append("quotation_file", uploadData.quotation_file);
        formData.append("quotation_refno", uploadData.quotation_refno);
        if (uploadData.po_file) formData.append("po_file", uploadData.po_file);
        formData.append("po_number", uploadData.po_number);
        if (uploadData.tax_invoice_file) formData.append("tax_invoice_file", uploadData.tax_invoice_file);
        formData.append("tax_invoice_refno", uploadData.tax_invoice_refno);

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            await axios.post(`${API_BASE_URL}/api/list/upload`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Content-Type': 'multipart/form-data'
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Documents uploaded successfully',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setShowUploadModal(false);
            fetchData();
        } catch (error) {
            console.error("Upload error:", error);
            Swal.fire("Error", "Failed to upload documents", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id,itemType = 'tax_invoice') => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this record and its associated journals!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const response = await axios.delete(`${API_BASE_URL}/api/list/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                     params: {
            type: itemType  // ADD THIS
        }
                });

                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message,
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    fetchData();
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to delete record', 'error');
                }
            } catch (error) {
                console.error("Delete error:", error);
                Swal.fire("Error", error.response?.data?.message || "Failed to delete record", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Import & Export Data</h4>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div className="search-box me-2 mb-2 d-inline-block">
                                        <div className="position-relative">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search..."
                                                value={searchQuery}
                                                onChange={handleSearch}
                                            />
                                            <FiSearch className="search-icon" />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-8 text-sm-end">
                                    <div className="d-flex align-items-center justify-content-sm-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-light border"
                                            onClick={handleExport}
                                            title="Export to Excel"
                                        >
                                            <i className="mdi mdi-file-excel text-success font-size-16"></i>
                                        </button>

                                        <input
                                            type="file"
                                            accept=".xlsx, .xls"
                                            id="importQuotationFile"
                                            onChange={handleImport}
                                            style={{ display: "none" }}
                                        />
                                        <label
                                            htmlFor="importQuotationFile"
                                            className="btn btn-light border mb-0 d-flex align-items-center justify-content-center"
                                            style={{ height: '37px', width: '42px' }}
                                            title="Import from Excel"
                                        >
                                            <i className="mdi mdi-upload text-primary font-size-16"></i>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Table className="table align-middle table-nowrap table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <th>Project</th>
                                            <th>Quotation Ref No</th>

                                            <th>PO Number</th>
                                            <th>Invoice Ref No</th>
                                            <th>Invoice Amount</th>
                                                                                        <th>Total Quotation Amount</th>

                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length > 0 ? (
                                            data.flatMap(item => {
                                                const invoices = item.tax_invoices?.length > 0
                                                    ? item.tax_invoices
                                                    : item.proforma_invoices || [];

                                                if (invoices.length === 0) {
                                                    return (
                                                        <tr key={item.id}>
                                                            <td>{item.date ? dayjs(item.date).format('DD MMM YYYY') : 'N/A'}</td>
                                                            <td>{item.project?.client?.name || item.deals?.[0]?.client?.name || 'N/A'}</td>
                                                            <td>{item.project?.name || item.deals?.[0]?.name || 'N/A'}</td>
                                                            <td>
                                                                {item.quotation_file ? (
                                                                    <Button
                                                                        variant="link"
                                                                        className="p-0 text-primary fw-medium"
                                                                        onClick={() => openPreview(item, 'quotation')}
                                                                    >
                                                                        {item.ref_no || 'View Document'}
                                                                    </Button>
                                                                ) : (
                                                                    item.ref_no || 'N/A'
                                                                )}
                                                            </td>

                                                            <td>
                                                                {item.deals?.[0]?.po_attachment || item.po_file ? (
                                                                    <Button
                                                                        variant="link"
                                                                        className="p-0 text-primary fw-medium"
                                                                        onClick={() => openPreview(item, 'po')}
                                                                    >
                                                                        {item.deals?.[0]?.po_number || 'View Document'}
                                                                    </Button>
                                                                ) : (
                                                                    item.deals?.[0]?.po_number || 'N/A'
                                                                )}
                                                            </td>
                                                            <td>N/A</td>
                                                            <td>N/A</td>
                                                                                                                        <td>{item.amount}</td>

                                                            <td>
                                                                <Dropdown>
                                                                    <Dropdown.Toggle variant="link" className="text-muted p-0 border-0 hide-arrow">
                                                                        <FiMoreVertical />
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu align="end">
                                                                        <Dropdown.Item onClick={() => openUploadModal(item)}>
                                                                            <FiUpload className="me-2" /> Upload Documents
                                                                        </Dropdown.Item>
                                                                        <Dropdown.Divider />
                                                                        <Dropdown.Item onClick={() => handleDelete(item.id)} className="text-danger">
                                                                            <FiTrash2 className="me-2" /> Delete
                                                                        </Dropdown.Item>
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return invoices.map((invoice, index) => (
                                                    <tr key={`${item.id}-${invoice.id}`}>
                                                        <td>{
                                                            item.deals?.[0]?.issue_date ?
                                                                dayjs(item.deals[0].issue_date).format('DD MMM YYYY') :
                                                                (item.date ? dayjs(item.date).format('DD MMM YYYY') : 'N/A')}</td>
                                                        <td>{item.project?.client?.name || item.deals?.[0]?.client?.name || 'N/A'}</td>
                                                        <td>{item.project?.name || item.deals?.[0]?.name || 'N/A'}</td>
                                                        <td>
                                                            {item.quotation_file ? (
                                                                <Button
                                                                    variant="link"
                                                                    className="p-0 text-dark fw-medium"
                                                                    onClick={() => openPreview(item, 'quotation')}
                                                                >
                                                                    {item.ref_no || 'View Document'}
                                                                </Button>
                                                            ) : (
                                                                item.ref_no || 'N/A'
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.deals?.[0]?.po_attachment || item.po_file ? (
                                                                <Button
                                                                    variant="link"
                                                                    className="p-0 text-dark fw-medium"
                                                                    onClick={() => openPreview(item, 'po')}
                                                                >
                                                                    {item.deals?.[0]?.po_number || 'View Document'}
                                                                </Button>
                                                            ) : (
                                                                item.deals?.[0]?.po_number || 'N/A'
                                                            )}
                                                        </td>
                                                        <td>
                                                            {invoice.tax_invoice_file ? (
                                                                <Button
                                                                    variant="link"
                                                                    className="p-0 text-dark fw-medium"
                                                                    onClick={() => openPreview(item, 'invoice')}
                                                                >
                                                                    {invoice.ref_no || 'View Document'}
                                                                </Button>
                                                            ) : (
                                                                invoice.ref_no || 'N/A'
                                                            )}
                                                        </td>
                                                        <td>{invoice.total_with_vat || invoice.amount || 0}</td>
                                                        <td>{item.amount}</td>
                                                        <td>
                                                            <Dropdown>
                                                                <Dropdown.Toggle variant="link" className="text-muted p-0 border-0 hide-arrow">
                                                                    <FiMoreVertical />
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu align="end">
                                                                    <Dropdown.Item onClick={() => openUploadModal(item)}>
                                                                        <FiUpload className="me-2" /> Upload Documents
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Divider />
                                                                    <Dropdown.Item                                     onClick={() => handleDelete(invoice.id, 'tax_invoice')} 
 className="text-danger">
                                                                        <FiTrash2 className="me-2" /> Delete
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                ));
                                            })
                                        ) : (
                                            <tr><td colSpan="9" className="text-center">No data found</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-end mt-4">
                                <ReactPaginate
                                    previousLabel={"<"}
                                    nextLabel={">"}
                                    breakLabel={"..."}
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={3}
                                    onPageChange={handlePageClick}
                                    containerClassName={"pagination pagination-rounded justify-content-end mb-2"}
                                    activeClassName={"active"}
                                    pageClassName={"page-item"}
                                    pageLinkClassName={"page-link"}
                                    previousClassName={"page-item"}
                                    previousLinkClassName={"page-link"}
                                    nextClassName={"page-item"}
                                    nextLinkClassName={"page-link"}
                                    breakClassName={"page-item"}
                                    breakLinkClassName={"page-link"}
                                    forcePage={currentPage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Documents Offcanvas */}
            <Offcanvas show={showUploadModal} onHide={() => setShowUploadModal(false)} placement="end" style={{ width: '500px' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Upload Documents</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form onSubmit={handleUploadSubmit}>
                        <div className="mb-4 p-3 border rounded">
                            <h6 className="fw-bold mb-3">Quotation</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Quotation Ref No</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="quotation_refno"
                                    value={uploadData.quotation_refno}
                                    onChange={handleInputChange}
                                    placeholder="Enter Quotation Ref No"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>File</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(e) => handleFileChange(e, "quotation_file")}
                                />
                            </Form.Group>

                        </div>

                        <div className="mb-4 p-3 border rounded">
                            <h6 className="fw-bold mb-3"> Order</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>PO Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="po_number"
                                    value={uploadData.po_number}
                                    onChange={handleInputChange}
                                    placeholder="Enter PO Number"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>File</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(e) => handleFileChange(e, "po_file")}
                                />
                            </Form.Group>

                        </div>

                        <div className="mb-4 p-3 border rounded">
                            <h6 className="fw-bold mb-3">Tax Invoice</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Tax Invoice Ref No</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="tax_invoice_refno"
                                    value={uploadData.tax_invoice_refno}
                                    onChange={handleInputChange}
                                    placeholder="Enter Tax Invoice Ref No"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>File</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(e) => handleFileChange(e, "tax_invoice_file")}
                                />
                            </Form.Group>

                        </div>

                        <div className="mt-4">
                            <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                                {loading ? "Uploading..." : "Save Uploads"}
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Document Preview Modal */}
            <Offcanvas
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                placement="end"
                style={{ width: '680px' }}
            >
                <Offcanvas.Header closeButton>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <Offcanvas.Title>
                                <FiFile className="me-2" />
                                {previewData.title} - {previewData.refNo}
                            </Offcanvas.Title>
                        </div>
                        <div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                href={previewData.fileUrl}
                                target="_blank"
                                download={getFileNameFromUrl(previewData.fileUrl)}
                                className="d-flex align-items-center"
                            >
                                <FiDownload className="me-1" />
                                Download
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body style={{ minHeight: '500px', maxHeight: '80vh', overflow: 'auto', paddingTop: 0 }}>
                    {previewData.previewType === 'pdf' && (
                        <div style={{ height: 'calc(80vh - 60px)', overflow: 'auto' }}>
                            <iframe
                                src={`${previewData.fileUrl}#toolbar=0&navpanes=0`}
                                title={previewData.title}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    backgroundColor: '#f8f9fa'
                                }}
                            />
                        </div>
                    )}

                    {previewData.previewType === 'image' && (
                        <div className="text-center" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
                            <img
                                src={previewData.fileUrl}
                                alt={previewData.title}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(80vh - 100px)',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                                className="img-fluid"
                            />
                        </div>
                    )}

                    {(previewData.previewType === 'doc' || previewData.previewType === 'other') && (
                        <div className="text-center p-5" style={{ height: 'calc(80vh - 100px)' }}>
                            <div className="d-flex flex-column justify-content-center align-items-center h-100">
                                <FiFile size={100} className="text-muted mb-3" />
                                <h5>Document Preview Not Available</h5>
                                <p className="text-muted mb-4">
                                    This file format cannot be previewed directly.
                                    Please download the file to view it.
                                </p>
                                <Button
                                    variant="primary"
                                    href={previewData.fileUrl}
                                    target="_blank"
                                    download={getFileNameFromUrl(previewData.fileUrl)}
                                    className="d-flex align-items-center"
                                >
                                    <FiDownload className="me-2" />
                                    Download Document
                                </Button>
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
                <div className="p-3 border-top">
                    <Button
                        variant="secondary"
                        onClick={() => setShowPreviewModal(false)}
                        className="w-100"
                    >
                        Close Preview
                    </Button>
                </div>
            </Offcanvas>

            <style>{`
                .search-icon {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    color: #adb5bd;
                }
                .search-box input {
                    padding-left: 35px;
                }
                .hide-arrow::after {
                    display: none !important;
                }
                .btn-link {
                    text-decoration: none;
                }
                .btn-link:hover {
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
};

export default ImportExportList;